import React from "react";
import PropTypes from "prop-types";
import { Box, MenuItem, TextField } from "@mui/material";
import {
  CitizenshipField,
  CountryField,
  AddressFields,
} from "../../../../../components/guardian/ManualEntryFields";
import PrincipalEmailSection from "./PrincipalEmailSection";
import PrincipalNameSuggestionDialog from "./PrincipalNameSuggestionDialog";
import {
  fieldError,
  showHighBondSsn,
  showManualOccupation,
  showRelationship,
} from "../../principalFormUtils";
import {
  splitEmploymentOccupation,
  joinEmploymentOccupation,
  getOccupationQuestionLabel,
} from "../../formHelpers";

/** Manual principal entry: name dialog, demographics, address, occupation, optional SSN. */
export default function PrincipalManualSection({
  cfg,
  index: i,
  guardian,
  guardians,
  data,
  updateData,
  globalData,
  errors,
  keys,
  gVal,
  nameSuggestionUi,
  allCities,
  allStates,
  loadingCitiesStates,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  handleGoogleCallback,
  handleZipCodeChange,
  formatSSN,
  nameSx,
  renderExtraBlocks,
}) {
  const {
    suggestionDialogOpen,
    suggestedPG,
    handlePGSuggestionClose,
    handlePGSuggestionDecline,
    handlePGSuggestionConfirm,
    nameOnChange,
  } = nameSuggestionUi;

  const probateStreetProps =
    cfg.attorneyUi === "probate"
      ? {
          inputProps: { maxLength: 255 },
          error: !!fieldError(errors, i, keys.address),
          helperText: fieldError(errors, i, keys.address),
        }
      : {};

  return (
    <Box className="grid grid-cols-1 gap-4">
      <TextField
        sx={nameSx}
        label="Name"
        value={gVal(guardian, keys, "name")}
        onChange={(e) => nameOnChange(e.target.value)}
        fullWidth
        required
        slotProps={{ htmlInput: { maxLength: 255 }}}
      />

      <PrincipalNameSuggestionDialog
        open={suggestionDialogOpen[i] || false}
        suggestedLabel={suggestedPG[i]?.label}
        attorneyUiIsProbate={cfg.attorneyUi === "probate"}
        onBackdropSafeClose={() => handlePGSuggestionClose(i)}
        onDecline={() => handlePGSuggestionDecline(i)}
        onConfirm={() => handlePGSuggestionConfirm(i)}
      />

      <PrincipalEmailSection
        cfg={cfg}
        index={i}
        guardian={guardian}
        guardians={guardians}
        globalData={globalData}
        keys={keys}
        gVal={gVal}
        data={data}
        updateData={updateData}
      />

      <CitizenshipField
        citizenshipSelectSx={cfg.attorneyUi === "probate" ? { mb: 2 } : undefined}
        specifyCitizenshipSx={cfg.attorneyUi === "probate" ? { mb: 2 } : undefined}
        value={gVal(guardian, keys, "citizenship")}
        customValue={gVal(guardian, keys, "customCitizenship")}
        onChange={(v) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[i] = {
            ...updatedGuardians[i],
            [keys.citizenship]: v === "Other" ? "Other" : v,
          };
          updateData({ ...data, guardians: updatedGuardians });
        }}
        onCustomChange={(v) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[i] = { ...updatedGuardians[i], [keys.customCitizenship]: v };
          updateData({ ...data, guardians: updatedGuardians });
        }}
      />

      <CountryField
        countrySelectSx={cfg.attorneyUi === "probate" ? { mb: 2 } : undefined}
        specifyCountrySx={cfg.attorneyUi === "probate" ? { mb: 2 } : undefined}
        value={gVal(guardian, keys, "country")}
        customValue={gVal(guardian, keys, "customCountry")}
        onChange={(v) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[i] = {
            ...updatedGuardians[i],
            [keys.country]: v === "Other" ? "Other" : v,
          };
          updateData({ ...data, guardians: updatedGuardians });
        }}
        onCustomChange={(v) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[i] = { ...updatedGuardians[i], [keys.customCountry]: v };
          updateData({ ...data, guardians: updatedGuardians });
        }}
      />

      {showRelationship(cfg, guardian) && (
        <TextField
          label={cfg.relationshipLabel}
          value={gVal(guardian, keys, "relationship")}
          onChange={(e) => {
            const updatedGuardians = [...guardians];
            updatedGuardians[i] = {
              ...updatedGuardians[i],
              [keys.relationship]: e.target.value,
            };
            updateData({ ...data, guardians: updatedGuardians });
          }}
          fullWidth
          required={cfg.relationshipRequired}
        />
      )}

      <AddressFields
        address={gVal(guardian, keys, "address")}
        zipCode={gVal(guardian, keys, "zip")}
        city={gVal(guardian, keys, "city")}
        state={gVal(guardian, keys, "state")}
        googlePlaceholder={cfg.googlePlaceholderManual}
        onGoogleCallback={(address, city, stateVal, zipCode) =>
          handleGoogleCallback(i, address, city, stateVal, zipCode)
        }
        onAddressChange={(v) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[i] = { ...updatedGuardians[i], [keys.address]: v };
          updateData({ ...data, guardians: updatedGuardians });
        }}
        onZipChange={(e) => handleZipCodeChange(e, i)}
        onCityChange={(v) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[i] = { ...updatedGuardians[i], [keys.city]: v };
          updateData({ ...data, guardians: updatedGuardians });
        }}
        onStateChange={(v) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[i] = { ...updatedGuardians[i], [keys.state]: v };
          updateData({ ...data, guardians: updatedGuardians });
        }}
        loading={loadingCitiesStates}
        allCities={allCities}
        allStates={allStates}
        GoogleAutoComplete={GoogleAutoComplete}
        LabeledCreatableSelect={LabeledCreatableSelect}
        streetTextFieldProps={probateStreetProps}
      />

      {renderExtraBlocks?.(i, guardian, guardians)}

      {showHighBondSsn(cfg, globalData) && (
        <TextField
          label="Social Security Number (SSN)"
          type="password"
          value={gVal(guardian, keys, "ssn")}
          onChange={(e) => {
            const updatedGuardians = [...guardians];
            updatedGuardians[i] = { ...updatedGuardians[i], [keys.ssn]: e.target.value };
            updateData({ ...data, guardians: updatedGuardians });
          }}
          fullWidth
          required
          helperText={formatSSN(gVal(guardian, keys, "ssn"))}
        />
      )}

      {(() => {
        if (!showManualOccupation(cfg, globalData, guardian)) return null;
        const { employmentStatus, occupationText } = splitEmploymentOccupation(
          gVal(guardian, keys, "occupation")
        );
        const questionLabel = getOccupationQuestionLabel(employmentStatus);

        return (
          <>
            <TextField
              select
              label="Employment Status"
              value={employmentStatus || ""}
              onChange={(e) => {
                const updatedGuardians = [...guardians];
                updatedGuardians[i] = {
                  ...updatedGuardians[i],
                  [keys.occupation]: joinEmploymentOccupation(
                    e.target.value,
                    occupationText
                  ),
                };
                updateData({ ...data, guardians: updatedGuardians });
              }}
              fullWidth
              required
            >
              <MenuItem value="Employed">Employed</MenuItem>
              <MenuItem value="Unemployed">Unemployed</MenuItem>
              <MenuItem value="Retired">Retired</MenuItem>
            </TextField>
            {(employmentStatus === "Employed" || employmentStatus === "Retired") && (
              <TextField
                label={questionLabel}
                value={occupationText || ""}
                onChange={(e) => {
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                    [keys.occupation]: joinEmploymentOccupation(
                      employmentStatus,
                      e.target.value
                    ),
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                }}
                fullWidth
                required
              />
            )}
          </>
        );
      })()}
    </Box>
  );
}

PrincipalManualSection.propTypes = {
  cfg: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.object.isRequired,
  updateData: PropTypes.func.isRequired,
  globalData: PropTypes.object,
  errors: PropTypes.object,
  keys: PropTypes.object.isRequired,
  gVal: PropTypes.func.isRequired,
  nameSuggestionUi: PropTypes.shape({
    suggestionDialogOpen: PropTypes.array,
    suggestedPG: PropTypes.array,
    handlePGSuggestionClose: PropTypes.func,
    handlePGSuggestionDecline: PropTypes.func,
    handlePGSuggestionConfirm: PropTypes.func,
    nameOnChange: PropTypes.func,
  }).isRequired,
  allCities: PropTypes.array.isRequired,
  allStates: PropTypes.array.isRequired,
  loadingCitiesStates: PropTypes.bool.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  handleGoogleCallback: PropTypes.func.isRequired,
  handleZipCodeChange: PropTypes.func.isRequired,
  formatSSN: PropTypes.func.isRequired,
  renderExtraBlocks: PropTypes.func,
};
